export class ProcessInvoiceImage {
  
  // Función principal que ejecuta todo el flujo
  public static async run(file: File): Promise<File> {
    try {
      // Solo procesar imágenes
      if (!file.type.startsWith('image/')) {
        return file
      }

      // Convertir archivo a imagen
      const img = await this.fileToImage(file)

      // Redimensionar y comprimir
      const finalFile = await this.resizeAndCompress(img, file.name)

      return finalFile
    } catch (error) {
      console.error('Error en ProcessInvoiceImage:', error)
      // Si hay error, devolver archivo original
      return file
    }
  }

  // Convertir File a ImageData
  private static async fileToImage(file: File): Promise<ImageData> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = img.width
        canvas.height = img.height
        ctx?.drawImage(img, 0, 0)
        const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height)
        if (imageData) {
          resolve(imageData)
        } else {
          reject(new Error('No se pudo obtener ImageData'))
        }
      }
      img.onerror = () => reject(new Error('Error al cargar la imagen'))
      img.src = URL.createObjectURL(file)
    })
  }


  // Redimensionar y comprimir a JPEG
  private static async resizeAndCompress(imageData: ImageData, fileName: string): Promise<File> {
    return new Promise((resolve, reject) => {
      // Crear canvas temporal para convertir ImageData a Image
      const tempCanvas = document.createElement('canvas')
      const tempCtx = tempCanvas.getContext('2d')
      
      if (!tempCtx) {
        reject(new Error('No se pudo obtener contexto del canvas temporal'))
        return
      }
      
      // Configurar canvas temporal con el tamaño original
      tempCanvas.width = imageData.width
      tempCanvas.height = imageData.height
      tempCtx.putImageData(imageData, 0, 0)
      
      // Crear imagen desde el canvas temporal
      const img = new Image()
      img.onload = () => {
        // Crear canvas final para redimensionamiento
        const finalCanvas = document.createElement('canvas')
        const finalCtx = finalCanvas.getContext('2d')
        
        if (!finalCtx) {
          reject(new Error('No se pudo obtener contexto del canvas final'))
          return
        }
        
        // Calcular nuevas dimensiones manteniendo ratio
        let { width, height } = img
        const maxWidth = 2200
        const maxHeight = 2200
        
        // Redimensionar si es necesario
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = Math.round(width * ratio)
          height = Math.round(height * ratio)
        }
        
        // Configurar canvas final
        finalCanvas.width = width
        finalCanvas.height = height
        
        // Aplicar filtros de suavizado para mejor calidad
        finalCtx.imageSmoothingEnabled = true
        finalCtx.imageSmoothingQuality = 'high'
        
        // Dibujar imagen redimensionada
        finalCtx.drawImage(img, 0, 0, width, height)
        
        // Convertir a JPEG con compresión
        finalCanvas.toBlob((blob) => {
          if (blob) {
            // Crear nuevo archivo JPEG
            const compressedFile = new File([blob], fileName.replace(/\.[^/.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now()
            })
            resolve(compressedFile)
          } else {
            reject(new Error('Error al comprimir la imagen'))
          }
        }, 'image/jpeg', 0.85) // Calidad del 85%
      }
      
      img.onerror = () => {
        reject(new Error('Error al cargar la imagen para redimensionamiento'))
      }
      
      // Convertir canvas a data URL para crear la imagen
      img.src = tempCanvas.toDataURL('image/png')
    })
  }

}

// Función global para cuando OpenCV esté listo
declare global {
  interface Window {
    onOpenCvReady: () => void
  }
}

window.onOpenCvReady = () => {
  console.log('OpenCV.js está listo')
}